import { useState } from 'react';
import { CloseCircleOutlined, MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Die } from '@prisma/client';
import { Button, ButtonProps, ModalProps, Result, Spin } from 'antd';
import Descriptions, { DescriptionsItemType } from 'antd/es/descriptions';

import { ProjectWithDetails } from '@app/domain-t3-interfaces';

import { useActivateProject, useDeactivateProject, useGetProject } from '../data';

export const useProjectActions = () => {
  const [deactivateId, setDeactivateId] = useState<number | undefined>();

  const activator = useActivateProject();
  const deactivator = useDeactivateProject();
  const deactivateProject = useGetProject(deactivateId ?? 0, { enabled: !!deactivateId });

  const toggleDeactivateModal = (id?: number) => {
    setDeactivateId(id);
  };

  const _getDescriptionItems = (data?: ProjectWithDetails): DescriptionsItemType[] => [
    { key: 'Project', children: data?.name, label: 'Project' },
    { key: 'Node', children: data?.node?.name, label: 'Node' },
  ];

  const getActionButton = (record: Die, props?: ButtonProps) =>
    record.t3Active ? (
      <Button
        icon={<MinusCircleOutlined />}
        size="small"
        {...props}
        onClick={() => {
          toggleDeactivateModal(record.id);
        }}
        danger
        loading={deactivator.isPending}
        disabled={deactivator.isPending}
      >
        Deactivate
      </Button>
    ) : (
      <Button
        icon={<PlusCircleOutlined />}
        size="small"
        {...props}
        onClick={() => {
          activator.mutate(record.id);
        }}
        type="primary"
        loading={activator.isPending}
        disabled={deactivator.isPending}
      >
        Activate
      </Button>
    );

  const getDeactivateModalProps = (props?: ModalProps): ModalProps => {
    const onCancel = () => {
      deactivator.reset();
      toggleDeactivateModal();
    };
    const onOk = () => {
      if (deactivator.isIdle || deactivator.isError) {
        deactivateId && deactivator.mutate(deactivateId);
      } else {
        onCancel();
      }
    };
    return {
      centered: true,
      width: 600,
      title: 'Are you sure you want to deactivate this project for T3?',
      ...props,
      children: deactivator.isPending ? (
        <Spin style={{ width: '100%' }} />
      ) : deactivator.isSuccess ? (
        <Result
          status={'success'}
          title={'Successfully deactivated project for T3'}
          subTitle={`Project ${deactivateProject.data?.name} has been deactivated for node ${deactivateProject.data?.node?.name}.`}
        />
      ) : deactivator.isError ? (
        <Result
          status={'error'}
          title="Error occured when deactivating project"
          subTitle={deactivator.error.response?.data.message}
        />
      ) : (
        <Descriptions
          bordered
          size="small"
          layout="horizontal"
          column={1}
          labelStyle={{ width: 150 }}
          items={_getDescriptionItems(deactivateProject.data)}
        />
      ),
      cancelButtonProps: {
        style: deactivator.isSuccess ? { display: 'none' } : undefined,
        disabled: deactivator.isPending,
      },
      okButtonProps: {
        icon: deactivator.isSuccess ? <CloseCircleOutlined /> : <MinusCircleOutlined />,
        disabled: deactivator.isPending,
      },
      okText: deactivator.isIdle || deactivator.isPending ? 'Deactivate' : deactivator.isError ? 'Retry' : 'Close',
      okType: deactivator.isSuccess ? undefined : 'danger',
      open: !!deactivateId,
      onCancel,
      onOk,
    };
  };

  return {
    getActionButton,
    getDeactivateModalProps,
  };
};
